import DOMPurify from 'dompurify'

import type { IIcons } from '@foundever/ui-lib'

export enum TOAST_POSITION {
  TOP_RIGHT = 'top-right',
  TOP_LEFT = 'top-left',
  TOP_CENTER = 'top-center',
  BOTTOM_RIGHT = 'bottom-right',
  BOTTOM_LEFT = 'bottom-left',
}

export enum TOAST_TYPE {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  CAUTION = 'caution',
  INFO = 'info',
  BASIC = 'basic',
}

export type IToastOptions = {
  title?: string
  type?: TOAST_TYPE
  timeout?: number | 'none'
}

export interface IToast extends IToastOptions {
  id: string
  message: string
  type: TOAST_TYPE
}

export type IFeedbackTheme = {
  theme: 'success' | 'warning' | 'error' | 'custom'
  icon?: IIcons
}

// Maps the store's feedback types to ui-lib `UIToast` / `UIAlert` themes. Types
// ui-lib has no theme for use `custom` + the matching icon.
export const FEEDBACK_THEME_MAP: Record<TOAST_TYPE, IFeedbackTheme> = {
  [TOAST_TYPE.SUCCESS]: { theme: 'success' },
  [TOAST_TYPE.ERROR]: { theme: 'error' },
  [TOAST_TYPE.WARNING]: { theme: 'warning' },
  [TOAST_TYPE.CAUTION]: { theme: 'custom', icon: 'circleExclamationSolid' },
  [TOAST_TYPE.INFO]: { theme: 'custom', icon: 'circleInfoSolid' },
  [TOAST_TYPE.BASIC]: { theme: 'custom', icon: 'circleUserSolid' },
}

// ui-lib renders `description` as plain text, so the message is sanitized and
// reduced to its visible text (tags dropped, entities decoded).
export const toPlainText = (message: string): string => {
  const clean = DOMPurify.sanitize(message.replace(/<br\s*\/?>/gi, ' '), {
    ALLOWED_TAGS: [],
  })
  return (
    new DOMParser().parseFromString(clean, 'text/html').body.textContent ?? ''
  )
}

// ui-lib's dismiss button is icon-only with no accessible name; label it.
export const labelDismissButtons = (root: HTMLElement | null): void => {
  root
    ?.querySelectorAll('.UIAlert__dismiss')
    .forEach((el) => el.setAttribute('aria-label', 'Close'))
}
